import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  addDqlCondition,
  bareField,
  buildDql,
  createSearchApi,
  detectDialect,
  displayValue,
  fieldNamed,
  formatCell,
  formatEvents,
  compactEvents,
  isFailedState,
  isLogEventField,
  isTerminal,
  parseLogEvent,
  populatedFields,
  SearchQueryError,
} from '@bloo-team/api-client'
import type { Dialect, SearchApi, SearchResultPage, SearchRow, SearchWindow, TaskState } from '@bloo-team/api-client'
import { client } from './api'
import { isDemoSession } from './demo'
import { DEMO_CHAIN_AGO, DEMO_NOW } from './demo-time'

/**
 * Search — the async query job, wired to the page's lifecycle.
 *
 * Every other screen here is one useQuery. Search cannot be, because the result
 * does not arrive from the call that starts it:
 *
 *   invoke   useMutation  — creates server work, so retry is OFF. A retry would
 *                           submit the query twice and leave an orphan task.
 *   state    useQuery     — polls on an interval that STOPS at a terminal state.
 *   result   useQuery     — enabled only once the task succeeded. Keyed by page,
 *                           and immutable: paging re-reads a stored result by
 *                           task id, it never re-runs the query, so pages cache
 *                           forever.
 *
 * Two things this owns that are easy to miss (both from Pattern 6 in
 * saas-console-docs/dataflow/pages/_patterns.md):
 *
 * 1. REVOKE. An abandoned task keeps running server-side. This revokes on
 *    unmount and before starting a replacement search — but only while the task
 *    is still running, since revoking a finished task is pure noise.
 * 2. COLUMNS ARE FROZEN PER TASK. See `columns` below.
 */

export const liveSearchApi = createSearchApi(client)

export { addDqlCondition, bareField, buildDql, compactEvents, detectDialect, displayValue, fieldNamed, formatCell, formatEvents, isFailedState, isLogEventField, isTerminal, parseLogEvent, SearchQueryError }
export { facetDql, parseDql, populatedFields, queryDuration, stripDuration, timelineDql, timelineSplitDql } from '@bloo-team/api-client'
export type { TimesliceForm } from '@bloo-team/api-client'
export {
  AGG_FUNCTIONS, DQL_OPERATORS, DURATION_PRESETS,
} from '@bloo-team/api-client'
export { timeLabel, toApiTime, DURATION_UNITS } from '@bloo-team/api-client'
export type { DqlAggregation, DqlFilter, DqlTime, SearchWindow } from '@bloo-team/api-client'
export type { Dialect, SearchResultPage, SearchRow, TaskState }

/** What the query box starts with, and what the placeholder teaches. */
export const EXAMPLE_QUERY = 'stream=SIGNALS | duration 1d | limit 100'

/* ---------------------------------------------------------------- demo mode */

/**
 * VITE_AUTH_MODE defaults to demo, so this is the path most people see first.
 *
 * The fixture is built to exercise the parts of the UI that the happy path
 * would otherwise hide: `fields_list` is far wider than the populated column
 * set (the real ratio is ~294:33), one column is a JSON-encoded array, one is a
 * boolean that is false, and timestamps are epoch milliseconds.
 */
const DEMO_POPULATED = [
  'CNAMTime', 'Stream', 'SourceType', 'SourceName', 'System',
  'Action', 'SrcIP', 'SrcPort', 'DstIP', 'DstPort', 'Proto',
  'TXLen', 'RXLen', 'DstCN', 'ExtractorID', 'Blocked', 'RelatedStreams',
  // The raw line, present so the demo exercises the icon-not-text rendering.
  'LogEvent',
]

/** The columns the stream *can* return but this result set does not use. */
const DEMO_EMPTY = [
  'AccessGranted', 'AccessRemoved', 'AccountDomain', 'AccountID', 'AccountUPN',
  'AddedCAPs', 'AddedCryptoSetName', 'App', 'AppProto', 'Application',
  'AuditPolicyChanges', 'ConnectionRequestPolicyName', 'ErrorCode',
  'MaximumTTL', 'ModifiedAuthenticationSetName', 'ObjectType', 'ParentImage',
  'ShareName', 'SubjectDomain', 'TargetFilename',
]

export const DEMO_FIELDS = [...DEMO_POPULATED, ...DEMO_EMPTY]

const DEMO_BASE = 1787321880000

export const DEMO_ROWS: SearchRow[] = Array.from({ length: 137 }, (_, i) => {
  const blocked = i % 3 === 0
  const row: SearchRow = {
    CNAMTime: DEMO_BASE - i * 60_000,
    System: `edge-fw-0${(i % 4) + 1}`,
    SourceName: 'FORTIGATE',
    SourceType: 'FIREWALL',
    Stream: 'FIREWALL',
    Action: blocked ? 'PACKET_BLOCKED' : 'PACKET_ALLOWED',
    SrcIP: `10.0.${i % 12}.${(i * 7) % 250}`,
    SrcPort: 32000 + (i % 4000),
    DstIP: `192.168.${i % 6}.${(i * 3) % 250}`,
    DstPort: [443, 80, 22, 3389, 53][i % 5],
    Proto: i % 5 === 4 ? 'UDP' : 'TCP',
    TXLen: 200 + ((i * 37) % 9000),
    RXLen: 100 + ((i * 91) % 4000),
    DstCN: ['US', 'DE', 'IN', 'SG', 'BR'][i % 5],
    // Shared with the Datafabric fixtures (sources + extractors): 4001 = fortinet-fortigate-syslog_kv.
    ExtractorID: 4001,
    // A false boolean must read as "false", not as an empty cell.
    Blocked: blocked,
    // Nested arrays come back JSON-encoded INTO a string. Verbatim from live.
    RelatedStreams: '["EP-PROCESS","WIN-AUDIT"]',
    // Raw log line — deliberately long, like the real thing: rendering these
    // inline is exactly the table-performance problem the icon solves.
    LogEvent:
      `<190>date=2026-08-21 time=08:${String(18 - (i % 18)).padStart(2, '0')}:00 devname="edge-fw-0${(i % 4) + 1}" devid="FGT60F0000000000" logid="0000000013" type="traffic" subtype="forward" level="notice" srcip=10.0.${i % 12}.${(i * 7) % 250} srcport=${32000 + (i % 4000)} srcintf="port1" dstip=192.168.${i % 6}.${(i * 3) % 250} dstport=${[443, 80, 22, 3389, 53][i % 5]} dstintf="port2" proto=6 action="${blocked ? 'deny' : 'accept'}" policyid=42 service="HTTPS" trandisp="snat" duration=180 sentbyte=${200 + ((i * 37) % 9000)} rcvdbyte=${100 + ((i * 91) % 4000)} sentpkt=12 rcvdpkt=9 appcat="unscanned" crscore=${blocked ? 30 : 0} craction=${blocked ? 131072 : 0}`,
  }
  for (const f of DEMO_EMPTY) row[f] = null
  return row
})

/**
 * A second fixture shaped like the SIGNALS stream, whose column vocabulary is
 * NOT the firewall one: no System, no SourceName, no Stream, and every name
 * lowercased. Its identity block is therefore two columns wide, not five.
 *
 * It exists because the FIREWALL fixture alone hid a real bug — the row detail
 * read the stream label out of `columns[4]`, which only happens to be `Stream`
 * when all five identity fields are present. SIGNALS is also what
 * EXAMPLE_QUERY runs, so it is the first thing anyone sees.
 */
export const DEMO_SIGNAL_FIELDS = [
  'cnamtime', 'sourcetype', 'uuid', 'scope', 'detectionseverity',
  'detectionname', 'detectiontactic', 'occurrences', 'falsepositive',
  'sourcestream', 'suspectuser', 'targethost',
  // Present in the vocabulary, empty in this result set.
  'suspecthash', 'suspecturl', 'suspectlocation', 'targetresource', 'signal_remark',
]

export const DEMO_SIGNAL_ROWS: SearchRow[] = Array.from({ length: 23 }, (_, i) => ({
  cnamtime: DEMO_BASE - i * 900_000,
  sourcetype: 'Workbook',
  uuid: `9f${i.toString(16).padStart(2, '0')}-signal`,
  scope: 'gotham',
  detectionseverity: ['High', 'Medium', 'Low'][i % 3],
  detectionname: [
    'Execute command writing output to local Admin Share',
    'Brute force access',
    'Login outside IN',
  ][i % 3],
  detectiontactic: ['Lateral Movement', 'Credential Access', 'Initial Access'][i % 3],
  occurrences: 1 + i * 37,
  falsepositive: false,
  sourcestream: '["EP-PROCESS","WIN-AUDIT"]',
  suspectuser: 'IEWIN7\\IEUser',
  targethost: `MSEDGEWIN${10 + (i % 3)}`,
  suspecthash: null,
  suspecturl: null,
  suspectlocation: null,
  targetresource: null,
  signal_remark: null,
}))

/**
 * A SearchApi over the fixture, so the hook below has exactly one code path.
 * It fakes the *timing* too — the first state poll reports STARTED — because a
 * demo that resolves instantly never shows the running state or the Cancel
 * button, which is most of what this screen's behaviour actually is.
 */
/**
 * Apply a query's `where` conditions to the fixture rows. The builder's main
 * demo interaction is adding a filter; if the fixture ignored `where`, that
 * interaction would visibly return rows the filter excludes, which reads as
 * the feature being broken. Parses only the conditions buildDql emits.
 */
function applyDemoWhere(query: string, rows: SearchRow[]): SearchRow[] {
  const where = / where (.+?) \| /.exec(query)?.[1]
  if (!where) return rows
  const conds = [...where.matchAll(/([\w-]+) (=|!=|>|<|LIKE|NOT LIKE) ('([^']*)'|[\w.'-]+)/g)]
  if (!conds.length) return rows
  // The entity search emits `A = x OR B = x`; the builder emits AND chains.
  // Mixed forms are not produced by anything here.
  const anyOf = /\bOR\b/.test(where)
  return rows.filter((row) =>
    conds[anyOf ? 'some' : 'every']((m) => {
      const [, field, op, rawVal, quoted] = m
      const want = quoted ?? rawVal!
      const have = row[field!]
      switch (op) {
        case '=': return String(have) === want
        case '!=': return String(have) !== want
        case '>': return Number(have) > Number(want)
        case '<': return Number(have) < Number(want)
        case 'LIKE': return String(have).includes(want.replace(/%/g, ''))
        case 'NOT LIKE': return !String(have).includes(want.replace(/%/g, ''))
        default: return true
      }
    }),
  )
}

/**
 * WIN-AUDIT and DNS fixtures: small, but ANSWERABLE — the stream picker is
 * ranked by ingest volume, which puts these ahead of FIREWALL, and a demo
 * whose default stream errors on Run reads as the whole screen being broken
 * (observed exactly that way).
 */
export const DEMO_WINAUDIT_ROWS: SearchRow[] = Array.from({ length: 61 }, (_, i) => ({
  CNAMTime: DEMO_BASE - i * 120_000,
  System: `dc-0${(i % 2) + 1}`,
  ExtractorID: 4002, // ms-windows-winlogbeat (Datafabric fixture id)
  EID: [4624, 4625, 4672][i % 3],
  User: ['administrator', 'svc-backup', 'jsmith'][i % 3],
  LogEvent: `EventID=${[4624, 4625, 4672][i % 3]} Account=${['administrator', 'svc-backup', 'jsmith'][i % 3]} LogonType=${i % 2 ? 3 : 10} Source=dc-0${(i % 2) + 1}`,
}))

export const DEMO_DNS_ROWS: SearchRow[] = Array.from({ length: 45 }, (_, i) => ({
  CNAMTime: DEMO_BASE - i * 180_000,
  System: `resolver-0${(i % 2) + 1}`,
  Query: ['example.com', 'update.bloo.io', 'cdn.fortinet.com'][i % 3],
  QueryType: i % 4 === 3 ? 'AAAA' : 'A',
  Answer: `93.184.21.${(i * 7) % 250}`,
}))

/**
 * EP-PROCESS: a sysmon-shaped fixture with parent/child process fields, so
 * the signal deep panel's causality tree has something to build from. Each
 * host carries one short chain (explorer -> office -> powershell -> tool) —
 * the `Suspicious PowerShell Execution` and LSASS signals point here.
 */
const EP_HOSTS = Object.keys(DEMO_CHAIN_AGO)
export const DEMO_EP_FIELDS = ['CNAMTime', 'System', 'ExtractorID', 'User', 'ProcessId', 'ParentProcessId', 'Image', 'ParentImage', 'CommandLine', 'TargetImage', 'LogEvent']
export const DEMO_EP_ROWS: SearchRow[] = EP_HOSTS.flatMap((host, h) => {
  // Now-relative, unlike the other fixtures: these rows exist to sit inside
  // a signal's ±15 min window, and the signals fixture is now-relative.
  const base = DEMO_NOW - DEMO_CHAIN_AGO[host]! * 60_000
  const pid = 4000 + h * 100
  const chain = [
    { pid: pid + 1, ppid: 900, image: 'C:\\Windows\\explorer.exe', parent: 'C:\\Windows\\System32\\userinit.exe', cmd: 'explorer.exe' },
    { pid: pid + 2, ppid: pid + 1, image: 'C:\\Program Files\\Microsoft Office\\WINWORD.EXE', parent: 'C:\\Windows\\explorer.exe', cmd: '"WINWORD.EXE" /n invoice_Q3.docm' },
    { pid: pid + 3, ppid: pid + 2, image: 'C:\\Windows\\System32\\WindowsPowerShell\\v1.0\\powershell.exe', parent: 'C:\\Program Files\\Microsoft Office\\WINWORD.EXE', cmd: 'powershell.exe -nop -w hidden -enc SQBFAFgAIAAoAE4AZQB3AC0ATwBiAGoAZQBjAHQA' },
    { pid: pid + 4, ppid: pid + 3, image: 'C:\\Users\\Public\\procdump.exe', parent: 'C:\\Windows\\System32\\WindowsPowerShell\\v1.0\\powershell.exe', cmd: 'procdump.exe -ma lsass.exe C:\\Users\\Public\\l.dmp', target: 'C:\\Windows\\System32\\lsass.exe' },
  ]
  return chain.map((p, i) => ({
    CNAMTime: base - (chain.length - i) * 20_000,
    ExtractorID: 4004, // broadcom-symantec-endpoint-syslog_kv (Datafabric fixture id)
    System: host,
    User: h === 0 ? 'CORP\\j.mendes' : h === 3 ? 'CORP\\ci-runner' : 'CORP\\a.silva',
    ProcessId: p.pid,
    ParentProcessId: p.ppid,
    Image: p.image,
    ParentImage: p.parent,
    CommandLine: p.cmd,
    TargetImage: p.target ?? null,
    LogEvent: `EventID=1 UtcTime=${new Date(base - (chain.length - i) * 20_000).toISOString()} ProcessId=${p.pid} Image=${p.image} ParentProcessId=${p.ppid} ParentImage=${p.parent} CommandLine=${p.cmd}`,
  }))
})

/**
 * OTHER: where an extractor's `fallback:` sends lines none of its first-matches
 * caught (the captured YAMLs all annotate `Stream: OTHER` there). Only the
 * raw line, the source identity and the extractor id survive — that is the
 * point: these are the events a parser is NOT parsing. Fabric's health board
 * counts them per ExtractorID (docs/extractors-requirements.md W1).
 */
export const DEMO_OTHER_FIELDS = ['CNAMTime', 'System', 'SourceName', 'SourceType', 'Stream', 'ExtractorID', 'EventName', 'LogEvent']
export const DEMO_OTHER_ROWS: SearchRow[] = Array.from({ length: 9 }, (_, i) => ({
  CNAMTime: DEMO_BASE - i * 300_000,
  System: `web-0${(i % 3) + 1}`,
  SourceName: 'NGINX',
  SourceType: 'APPLICATION',
  Stream: 'OTHER',
  ExtractorID: 4005, // nginx-access (Datafabric fixture id) — its fallback
  EventName: 'Generic nginx event',
  LogEvent: `2026/08/21 08:${String(59 - i).padStart(2, '0')}:10 [warn] 1234#0: *${9000 + i} upstream server temporarily disabled while connecting to upstream, client: 10.0.${i % 12}.7, server: web-0${(i % 3) + 1}`,
}))

const DEMO_ANSWERABLE: Record<string, { fields: string[]; rows: SearchRow[] }> = {
  FIREWALL: { fields: DEMO_FIELDS, rows: DEMO_ROWS },
  SIGNALS: { fields: DEMO_SIGNAL_FIELDS, rows: DEMO_SIGNAL_ROWS },
  'EP-PROCESS': { fields: DEMO_EP_FIELDS, rows: DEMO_EP_ROWS },
  'WIN-AUDIT': { fields: ['CNAMTime', 'System', 'ExtractorID', 'EID', 'User', 'LogEvent'], rows: DEMO_WINAUDIT_ROWS },
  DNS: { fields: ['CNAMTime', 'System', 'Query', 'QueryType', 'Answer'], rows: DEMO_DNS_ROWS },
  OTHER: { fields: DEMO_OTHER_FIELDS, rows: DEMO_OTHER_ROWS },
}

/**
 * Which fixture(s) a demo query reads. `stream=` takes a COMMA LIST
 * (`stream=FIREWALL,SIGNALS`), so this merges: field vocabularies union,
 * rows concatenate — the merged set is naturally sparse, exactly like a real
 * multi-stream result where each row only carries its own stream's columns.
 */
function demoSetFor(query: string, window?: SearchWindow) {
  const listed = (/stream\s*=\s*([\w*-]+(?:\s*,\s*[\w*-]+)*)/i.exec(query)?.[1] ?? '')
    .split(',').map((x) => x.trim().toUpperCase()).filter(Boolean)
  const wantsSignals = /\$Stream\s*=\s*SIGNALS/i.test(query)
  const names = listed.includes('*')
    ? Object.keys(DEMO_ANSWERABLE)
    : listed.length
      ? listed.filter((n) => n in DEMO_ANSWERABLE)
      : wantsSignals ? ['SIGNALS'] : ['FIREWALL']
  const chosen = names.length ? names : ['FIREWALL']
  const sets = chosen.map((n) => DEMO_ANSWERABLE[n]!)

  const base = {
    fields: [...new Set(sets.flatMap((x) => x.fields))],
    // Every row knows its stream — the cross-stream count (`stream=* |
    // groupby Stream`) reads it, and only the FIREWALL fixture spells it.
    rows: sets.flatMap((x, i) => x.rows.map((r) => ('Stream' in r ? r : { ...r, Stream: chosen[i] }))),
    stream: names.length ? names.join(',') : 'FIREWALL',
  }
  base.rows = applyDemoWhere(query, base.rows)
  // An absolute window filters on the event time, exactly what the backend's
  // start_time/end_time do. Parsed as LOCAL time to match what the picker sent.
  if (window) {
    const startMs = new Date(window.start).getTime()
    const endMs = new Date(window.end).getTime()
    base.rows = base.rows.filter((r) => {
      const t = Object.entries(r).find(([k]) => k.toLowerCase() === 'cnamtime')?.[1]
      return typeof t === 'number' && t >= startMs && t <= endMs
    })
  }

  // A timeslice count — the timeline's query shape. Buckets each row's
  // cnamtime; the bucket column carries whichever name was grouped by, so
  // BOTH probe forms (timeslice / CNAMTime) answer, like a permissive
  // engine. `groupby <bucket>, <field>` splits each bucket by that field —
  // the stacked timeline's shape.
  const ts = /timeslice\s+(\d+)([smhd])/i.exec(query)
  const tg = /groupby\s+\$?(timeslice|cnamtime)\b(?:\s*,\s*\$?([\w-]+))?/i.exec(query)
  if (ts && tg) {
    const unit = { s: 1000, m: 60_000, h: 3_600_000, d: 86_400_000 }[ts[2]!.toLowerCase()]!
    const sliceMs = Number(ts[1]) * unit
    const field = tg[1]!.toLowerCase() === 'timeslice' ? 'timeslice' : 'CNAMTime'
    const splitName = tg[2]
    const split = splitName
      ? base.fields.map(bareField).find((f) => f.toLowerCase() === splitName.toLowerCase())
      : undefined
    const counts = new Map<string, number>()
    for (const row of base.rows) {
      const t = Object.entries(row).find(([k]) => k.toLowerCase() === 'cnamtime')?.[1]
      if (typeof t !== 'number') continue
      const b = Math.floor(t / sliceMs) * sliceMs
      // NUL separator: split values can contain spaces.
      const key = split ? `${b}\u0000${String(row[split] ?? '—')}` : String(b)
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
    const rows = [...counts.entries()]
      .sort((a, b) => Number(a[0].split('\u0000')[0]) - Number(b[0].split('\u0000')[0]))
      .map(([k, n]) => {
        const [t, v] = k.split('\u0000')
        return split
          ? { [field]: Number(t), [split]: v, count_col1: n }
          : { [field]: Number(t), count_col1: n }
      })
    return {
      fields: split ? [field, split, 'count_col1'] : [field, 'count_col1'],
      rows,
      stream: base.stream,
    }
  }

  // A groupby query must come back AGGREGATED, or the builder's main demo
  // path looks broken ("count by Action" answering with raw events). The
  // aggregate column gets the synthetic name the live engine uses
  // (`count_col1`), so the UI never learns to rely on the written one.
  const g = /groupby\s+([\w-]+)/i.exec(query)
  if (g) {
    const field = base.fields.map(bareField).find((f) => f.toLowerCase() === g[1]!.toLowerCase())
    if (field) {
      const counts = new Map<string, number>()
      for (const row of base.rows) {
        const key = String(row[field] ?? '—')
        counts.set(key, (counts.get(key) ?? 0) + 1)
      }
      const rows = [...counts.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([k, n]) => ({ [field]: k, count_col1: n }))
      return { fields: [field, 'count_col1'], rows, stream: base.stream }
    }
  }
  return base
}

export function createDemoSearchApi(): SearchApi {
  const polls = new Map<string, number>()
  const sets = new Map<string, ReturnType<typeof demoSetFor>>()
  /** Tasks whose SQL "succeeded" as a task but failed as a query. */
  const sqlFailed = new Set<string>()
  let seq = 0

  return {
    cost: async ({ query }) => (query.includes('duration 1d') ? 8580 : 214_902),

    invoke: async ({ query, dialect = 'dql', window }) => {
      const q = query.trim()
      if (!q) throw new SearchQueryError('Enter a query to run.', null, null)

      if (dialect === 'sql') {
        // The live asymmetry, reproduced: SQL is NEVER rejected at invoke.
        // A statement naming an unknown table still becomes a task; its
        // failure only shows up in the result (sqlFailed below).
        seq += 1
        const id = `demo-task-${seq}-id-0`
        polls.set(id, 0)
        const table = /from\s+([\w*-]+)/i.exec(q)?.[1]
        if (table && /^(SIGNALS|FIREWALL|WIN-AUDIT|DNS|EP-PROCESS|OTHER)$/i.test(table)) {
          sets.set(id, demoSetFor(`stream=${table.toUpperCase()}`))
        } else {
          sqlFailed.add(id)
        }
        return id
      }

      // Mirrors the two real DQL rejections so the error path works offline.
      if (!/^\s*(stream\s*=|sourcename\s*=|_fetch\b)/i.test(q)) {
        throw new SearchQueryError('Unexpected |', q, null)
      }
      // A comma list validates PER STREAM, first unknown wins — matching how
      // the live parser names the offender ("Unknown stream: X").
      const streamList = /stream\s*=\s*([\w*-]+(?:\s*,\s*[\w*-]+)*)/i.exec(q)?.[1]
      for (const st of (streamList ?? '').split(',').map((x) => x.trim()).filter(Boolean)) {
        if (!/^(\*|SIGNALS|FIREWALL|WIN-AUDIT|DNS|EP-PROCESS|OTHER)$/i.test(st)) {
          throw new SearchQueryError(`Unknown stream: ${st}`, null, null)
        }
      }
      seq += 1
      const id = `demo-task-${seq}-id-0`
      polls.set(id, 0)
      sets.set(id, demoSetFor(q, window))
      return id
    },

    state: async ({ taskId }) => {
      const n = (polls.get(taskId) ?? 0) + 1
      polls.set(taskId, n)
      return n < 2
        ? { status: 'success', task_state: 'STARTED', task_stage: null }
        : { status: 'success', task_state: 'SUCCESS', task_stage: 'EXECUTED' }
    },

    result: async ({ taskId, pageNo = 1, pageSize = 50 }) => {
      if (sqlFailed.has(taskId)) {
        // Verbatim shape of the live failure, vague message included.
        return {
          rows: [], fields: [], total: 0, executionTime: '0.0646 sec',
          sourceStreams: [], failed: true, message: 'Something went wrong!',
        }
      }
      const set = sets.get(taskId) ?? demoSetFor('')
      return {
        rows: set.rows.slice((pageNo - 1) * pageSize, pageNo * pageSize),
        fields: set.fields,
        total: set.rows.length,
        executionTime: '0.2184 sec',
        sourceStreams: set.stream.split(','),
        failed: false,
        message: null,
      }
    },

    revoke: async () => {},

    fields: async () => DEMO_VFIELDS,
  }
}

/**
 * The builder's vocabulary in demo mode: the same two streams the demo engine
 * can answer for, `$`-prefixed exactly as the live vfields map is, plus a few
 * streams that exist only as picker entries — choosing one produces the same
 * "Unknown stream" rejection a live typo would.
 */
export const DEMO_VFIELDS: Record<string, string[]> = {
  FIREWALL: DEMO_FIELDS.map((f) => `$${f}`),
  SIGNALS: DEMO_SIGNAL_FIELDS.map((f) => `$${f}`),
  'WIN-AUDIT': ['$CNAMTime', '$System', '$ExtractorID', '$EID', '$User', '$LogEvent'],
  DNS: ['$CNAMTime', '$System', '$Query', '$QueryType', '$Answer'],
  OTHER: DEMO_OTHER_FIELDS.map((f) => `$${f}`),
  'EP-PROCESS': DEMO_EP_FIELDS.map((f) => `$${f}`),
  // Picker-only: choosing it produces the same "Unknown stream" a live typo
  // would, keeping the rejection path reachable in demo.
  'EP-REGISTRY': ['$CNAMTime', '$System', '$TargetObject', '$Details', '$LogEvent'],
}

const demoSearchApi = createDemoSearchApi()

function apiFor(isDemo: boolean): SearchApi {
  return isDemo ? demoSearchApi : liveSearchApi
}

/** The demo or live SearchApi — for promise-shaped callers (entity search). */
export const searchApiFor = apiFor

/* ----------------------------------------------------------------- the hook */

export type SearchPhase = 'idle' | 'submitting' | 'running' | 'done' | 'failed'

export interface UseSearchJob {
  phase: SearchPhase
  /** The query currently backing `page`, which may lag the text in the box. */
  ranQuery: string | null
  /** Dialect of `ranQuery`, for labelling the result. */
  ranDialect: Dialect | null
  taskId: string | null
  page: SearchResultPage | undefined
  /**
   * The columns to render, frozen for this task id. Never recompute per page.
   */
  columns: string[]
  /** Events the query was estimated to scan, from realtimecost. */
  estimatedEvents: number | null
  /** A query the engine refused — show it inline, it is not a server failure. */
  queryError: SearchQueryError | null
  /** Anything else that went wrong. */
  error: Error | null
  pageNo: number
  pageSize: number
  isFetchingPage: boolean
  run: (query: string, window?: SearchWindow) => void
  cancel: () => void
  setPageNo: (n: number) => void
  setPageSize: (n: number) => void
  /**
   * Every row of the current task's stored result, paged through server-side
   * for export. Null when there is nothing exportable (no finished task, or
   * the task failed). `truncated` reports the EXPORT_MAX_ROWS cap.
   */
  exportAll: () => Promise<{ fields: string[]; rows: SearchRow[]; truncated: boolean } | null>
}

/**
 * Export pages through the stored result in bulk; the ceiling exists because
 * a typed query can carry any limit, and "export" must not become "download
 * the event store". The cap is surfaced to the user, never silent.
 */
export const EXPORT_MAX_ROWS = 50_000
const EXPORT_PAGE_SIZE = 1000

export function useSearchJob({
  clusterId,
  scopeId,
  initialPageSize = 50,
}: {
  clusterId: string
  scopeId: string
  /** First-page size — the timeline job reads its whole result in one page. */
  initialPageSize?: number
}): UseSearchJob {
  const isDemo = isDemoSession()
  const api = apiFor(isDemo)

  const [taskId, setTaskId] = useState<string | null>(null)
  const [ranQuery, setRanQuery] = useState<string | null>(null)
  /**
   * In-flight / rejected / failed are tracked HERE rather than read off the
   * mutation observer (`invoke.isPending`, `invoke.error`).
   *
   * OBSERVED, not theorised: running the app in the browser, after a query
   * submitted from the `?q=` mount effect, `invoke.status` stayed 'pending'
   * forever even though `onSuccess` had already fired and set the task id — so
   * the page sat on "submitting…" with a live Cancel button while rows were on
   * screen. Not reproducible under jsdom, including with StrictMode and the
   * same mount-effect sequence; the mechanism is unidentified.
   *
   * What IS established is that the mutation CALLBACKS fire reliably while the
   * observer's derived state does not. So everything the UI shows is captured
   * in onSuccess / onError / onSettled into state this hook owns. That also
   * covers `error`: reading it off the same untrustworthy observer would mean a
   * shared link with a bad query rendering no error at all.
   */
  const [submitting, setSubmitting] = useState(false)
  const [rejection, setRejection] = useState<SearchQueryError | null>(null)
  const [failure, setFailure] = useState<Error | null>(null)
  const [pageNo, setPageNo] = useState(1)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [estimatedEvents, setEstimatedEvents] = useState<number | null>(null)

  const queryClient = useQueryClient()
  const scope = { clusterId, scopeId }

  /* --- poll ------------------------------------------------------------- */

  const stateQuery = useQuery<TaskState>({
    queryKey: ['search-state', clusterId, scopeId, taskId],
    queryFn: () => api.state({ ...scope, taskId: taskId as string }),
    enabled: taskId !== null,
    // Stops the moment the task reaches any terminal state. An unknown state
    // counts as terminal (see isTerminal) so this can never spin forever.
    refetchInterval: (q) => (isTerminal(q.state.data?.task_state) ? false : 1000),
    // Keep polling while the tab is hidden. The job runs on the cluster whether
    // anyone is watching, and this interval already stops itself at a terminal
    // state, so there is no runaway traffic to guard against. With the default
    // (false) an analyst who switches tabs during a slow query comes back to a
    // page still saying "running" that will never resolve — observed exactly
    // that way against a backgrounded tab.
    refetchIntervalInBackground: true,
    gcTime: 0,
    retry: false,
  })

  const taskState = stateQuery.data?.task_state ?? null
  const settled = isTerminal(taskState)
  const failed = isFailedState(taskState)

  /* --- read ------------------------------------------------------------- */

  /**
   * Freeze the column set on the FIRST page returned for a task.
   *
   * Recomputing per page would let the table reflow as the user pages —
   * page 1 shows 33 columns, page 2 shows 31 — which reads as a bug even though
   * it is honest to the data. Later pages render the frozen set with blanks.
   *
   * The freeze lives in the query cache under the task id rather than in
   * component state: it is derived data with exactly the same lifetime as the
   * task, computing it in the queryFn keeps it out of the render path, and a
   * new task gets a new key rather than needing a reset.
   */
  const resultQuery = useQuery<SearchResultPage & { columns: string[] }>({
    queryKey: ['search-result', clusterId, scopeId, taskId, pageNo, pageSize],
    queryFn: async () => {
      const result = await api.result({ ...scope, taskId: taskId as string, pageNo, pageSize })
      const key = ['search-columns', taskId]
      const frozen = queryClient.getQueryData<string[]>(key)
      const columns = frozen ?? populatedFields(result.fields, result.rows)
      if (!frozen) queryClient.setQueryData(key, columns)
      return { ...result, columns }
    },
    enabled: taskId !== null && settled && !failed,
    // A completed task's stored result is immutable, so a page never goes
    // stale: flipping back to page 1 must not re-request it.
    staleTime: Infinity,
    retry: 1,
  })

  /* --- revoke ----------------------------------------------------------- */

  // Kept in a ref so the unmount cleanup sees the CURRENT task without
  // re-registering — a cleanup that re-registered on every state change would
  // revoke the task the moment it started running.
  const live = useRef<{ taskId: string | null; settled: boolean }>({ taskId: null, settled: false })
  useEffect(() => {
    live.current = { taskId, settled }
  }, [taskId, settled])

  const revokeIfRunning = useCallback(() => {
    const { taskId: id, settled: done } = live.current
    if (!id || done) return
    // Fire-and-forget: the user has already moved on, and a failed revoke must
    // not surface as an error on a screen they are leaving.
    void api.revoke({ ...scope, taskId: id }).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusterId, scopeId, isDemo])

  useEffect(() => revokeIfRunning, [revokeIfRunning])

  /* --- run -------------------------------------------------------------- */

  const invoke = useMutation<string, Error, { query: string; window?: SearchWindow }>({
    mutationFn: async ({ query, window }) => {
      // A POST that creates server work: a retry submits the query twice and
      // orphans the first task. Never retry this.
      revokeIfRunning()
      // Advisory only — a cost estimate must never stop a search from running.
      const dialect = detectDialect(query)
      void api
        .cost({ ...scope, query, dialect, window })
        .then(setEstimatedEvents)
        .catch(() => setEstimatedEvents(null))
      return api.invoke({ ...scope, query, dialect, window })
    },
    retry: 0,
    onSuccess: (id, { query }) => {
      setTaskId(id)
      setRanQuery(query)
      setPageNo(1)
    },
    onError: (e) => {
      // A refused query is domain data shown beside the box; anything else is a
      // failure banner. They are separate so a typo never reads as an outage.
      if (e instanceof SearchQueryError) setRejection(e)
      else setFailure(e)
    },
    onSettled: () => setSubmitting(false),
  })

  const run = useCallback(
    (query: string, window?: SearchWindow) => {
      setEstimatedEvents(null)
      setRejection(null)
      setFailure(null)
      setSubmitting(true)
      invoke.mutate({ query, window })
    },
    [invoke],
  )

  const cancel = useCallback(() => {
    revokeIfRunning()
    void stateQuery.refetch()
  }, [revokeIfRunning, stateQuery])

  /* --- export ------------------------------------------------------------ */

  /**
   * Re-reads the stored result in EXPORT_PAGE_SIZE pages rather than reusing
   * the on-screen page: the screen holds one page of a possibly much larger
   * result, and paging a stored result never re-runs the query (see module
   * comment), so this is cheap reads, not repeated searches.
   */
  const exportAll = useCallback(async () => {
    if (!taskId || !settled || failed) return null
    const first = await api.result({ clusterId, scopeId, taskId, pageNo: 1, pageSize: EXPORT_PAGE_SIZE })
    if (first.failed) return null
    const { fields, total } = first
    const rows = [...first.rows]
    for (let no = 2; rows.length < Math.min(total, EXPORT_MAX_ROWS); no++) {
      const page = await api.result({ clusterId, scopeId, taskId, pageNo: no, pageSize: EXPORT_PAGE_SIZE })
      if (page.failed || page.rows.length === 0) break
      rows.push(...page.rows)
    }
    return { fields, rows: rows.slice(0, EXPORT_MAX_ROWS), truncated: total > EXPORT_MAX_ROWS }
  }, [api, clusterId, scopeId, taskId, settled, failed])

  /* --- phase ------------------------------------------------------------ */

  /**
   * A page can be a failure even though the task succeeded: SQL errors are
   * invisible at invoke and at state (task_state says SUCCESS), surfacing only
   * as `failed` in the result body. Without this fold the page would render
   * "matched nothing" for a broken SELECT.
   */
  const sqlFailed = resultQuery.data?.failed === true

  const phase: SearchPhase = submitting
    ? 'submitting'
    : taskId === null
      ? 'idle'
      : failed || sqlFailed
        ? 'failed'
        : settled
          ? 'done'
          : 'running'

  return {
    phase,
    ranQuery,
    taskId,
    page: sqlFailed ? undefined : resultQuery.data,
    columns: sqlFailed ? [] : (resultQuery.data?.columns ?? []),
    estimatedEvents,
    queryError: rejection,
    error:
      failure ??
      stateQuery.error ??
      resultQuery.error ??
      (sqlFailed ? new Error(resultQuery.data?.message ?? 'The query failed to execute.') : null),
    ranDialect: ranQuery === null ? null : detectDialect(ranQuery),
    pageNo,
    pageSize,
    isFetchingPage: resultQuery.isFetching,
    run,
    cancel,
    setPageNo,
    setPageSize: (n: number) => {
      setPageSize(n)
      setPageNo(1)
    },
    exportAll,
  }
}

/* ----------------------------------------------------------- cost preview */

/**
 * What the query WOULD scan, before Run is pressed. The after-the-fact number
 * already lives in the job hook; this one exists because the moment the
 * estimate matters most is before committing — above all for SQL, which has
 * no duration stage and scans the whole store.
 *
 * Debounced so typing does not stream half-finished queries at realtimecost;
 * cached briefly so flipping between two candidate queries does not re-ask.
 * Advisory only, exactly like the in-run estimate: an error here renders as
 * no estimate, never as a page error.
 */
export function useCostPreview({
  clusterId,
  scopeId,
  query,
  window: win,
  enabled = true,
}: {
  clusterId: string
  scopeId: string
  query: string
  window?: SearchWindow
  enabled?: boolean
}): { events: number | null; pending: boolean } {
  const isDemo = isDemoSession()
  const api = apiFor(isDemo)

  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 500)
    return () => clearTimeout(t)
  }, [query])

  const q = useQuery<number | null>({
    queryKey: ['search-cost', clusterId, scopeId, debounced, win?.start ?? null, win?.end ?? null],
    queryFn: () =>
      api.cost({ clusterId, scopeId, query: debounced, dialect: detectDialect(debounced), window: win }),
    enabled: enabled && debounced !== '',
    staleTime: 60_000,
    gcTime: 60_000,
    retry: false,
  })

  return {
    events: q.data ?? null,
    // "Pending" includes the debounce gap, so the caller never shows a stale
    // number as if it described the query currently in the box.
    pending: enabled && query.trim() !== '' && (q.isFetching || debounced !== query.trim()),
  }
}

/* ------------------------------------------------------- builder vocabulary */

/**
 * The whole vocabulary — stream name -> `$`-prefixed field list — in one
 * vfields call. This is the piece the legacy editor spent six mount calls and
 * a readiness gate assembling; one call turns out to be enough, and the map
 * keys double as the stream picker.
 *
 * Cached long: the vocabulary changes when extractors change, not per search.
 */
export function useSearchFields({ clusterId, scopeId }: { clusterId: string; scopeId: string }) {
  const isDemo = isDemoSession()
  const api = apiFor(isDemo)
  return useQuery<Record<string, string[]>>({
    queryKey: ['search-fields', clusterId, scopeId, isDemo ? 'demo' : 'live'],
    queryFn: () => api.fields({ clusterId, scopeId }),
    staleTime: 15 * 60_000,
    retry: 1,
  })
}
